/**
 * 作業面のツールバー（§0.3.2「操作の発見容易性」）。
 * 取込・配列切替・ズーム・鎖・字幅・塩基色をここから辿れるようにする。
 */

import { useRef } from 'react';
import { Button, ButtonGroup, Dropdown, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { fullRange, zoomRange, type ViewRange } from '@kensuke/genome-core';
import type { StoredSequenceMeta } from '../../../services/geneAnalysisChunkStore.service';
import { MAX_SEQUENCE_FONT_SIZE, MIN_SEQUENCE_FONT_SIZE } from './geneAnalysisLayout';
import type { GeneAnalysisDisplayOptions } from './useGeneAnalysisWorkspace';

export const ACCEPTED_SEQUENCE_EXTENSIONS =
  '.fa,.fasta,.fna,.fas,.ffn,.frn,.seq,.txt,.gb,.gbk,.gbff,.genbank,.gproj';

export interface GeneAnalysisToolbarProps {
  sequences: StoredSequenceMeta[];
  activeSequenceId: string | null;
  onSelectSequence: (sequenceId: string) => void;
  view: ViewRange;
  onViewChange: (view: ViewRange) => void;
  genomeLength: number;
  display: GeneAnalysisDisplayOptions;
  onDisplayChange: (patch: Partial<GeneAnalysisDisplayOptions>) => void;
  onImportFile: (file: File) => void;
  importing: boolean;
  /** 塩基が読める縮尺か。塩基表示中の拡大は字幅を上げる（§0.3.1） */
  baseMode?: boolean;
  onSave?: () => void;
}

export default function GeneAnalysisToolbar({
  sequences,
  activeSequenceId,
  onSelectSequence,
  view,
  onViewChange,
  genomeLength,
  display,
  onDisplayChange,
  onImportFile,
  importing,
  baseMode = false,
  onSave,
}: GeneAnalysisToolbarProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const center = (view.start + view.end) / 2;
  const zoom = (factor: number) => onViewChange(zoomRange(view, factor, center, genomeLength));

  // 塩基表示中は範囲を狭めても見た目が変わらない（行が埋まる）ので、拡大は字幅で表す
  const zoomIn = () => {
    if (baseMode && display.fontSize < MAX_SEQUENCE_FONT_SIZE) {
      onDisplayChange({ fontSize: display.fontSize + 1 });
      return;
    }
    zoom(0.625);
  };

  return (
    <div className="ga-toolbar" data-testid="gene-analysis-toolbar">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_SEQUENCE_EXTENSIONS}
        className="d-none"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onImportFile(file);
          event.target.value = '';
        }}
      />

      <Button
        size="sm"
        variant="primary"
        onClick={() => fileInputRef.current?.click()}
        disabled={importing}
        data-testid="gene-analysis-import-button"
      >
        {t('geneAnalysis.toolbar.import')}
      </Button>
      {onSave && (
        <Button size="sm" variant="outline-primary" onClick={onSave} data-testid="gene-analysis-save">
          {t('geneAnalysis.toolbar.save')}
        </Button>
      )}

      {sequences.length > 1 && (
        <Form.Select
          size="sm"
          className="ga-toolbar-select"
          value={activeSequenceId ?? ''}
          onChange={(event) => onSelectSequence(event.target.value)}
          aria-label={t('geneAnalysis.toolbar.sequence')}
        >
          {sequences.map((meta) => (
            <option key={meta.sequenceId} value={meta.sequenceId}>
              {meta.name}（{meta.length.toLocaleString()} bp）
            </option>
          ))}
        </Form.Select>
      )}

      <ButtonGroup size="sm" aria-label={t('geneAnalysis.toolbar.zoom')}>
        <Button variant="outline-secondary" onClick={() => zoom(1.6)} title={t('geneAnalysis.toolbar.zoom_out')}>
          −
        </Button>
        <Button variant="outline-secondary" onClick={zoomIn} title={t('geneAnalysis.toolbar.zoom_in')}>
          ＋
        </Button>
        <Button variant="outline-secondary" onClick={() => onViewChange(fullRange(genomeLength))}>
          {t('geneAnalysis.toolbar.whole')}
        </Button>
      </ButtonGroup>

      <ButtonGroup size="sm" aria-label={t('geneAnalysis.toolbar.strand')}>
        <Button
          variant={display.strandMode === 'single' ? 'secondary' : 'outline-secondary'}
          onClick={() => onDisplayChange({ strandMode: 'single' })}
        >
          {t('geneAnalysis.toolbar.strand_single')}
        </Button>
        <Button
          variant={display.strandMode === 'double' ? 'secondary' : 'outline-secondary'}
          onClick={() => onDisplayChange({ strandMode: 'double' })}
        >
          {t('geneAnalysis.toolbar.strand_double')}
        </Button>
      </ButtonGroup>

      <ButtonGroup size="sm" aria-label={t('geneAnalysis.toolbar.font_size')}>
        <Button
          variant="outline-secondary"
          onClick={() =>
            onDisplayChange({ fontSize: Math.max(MIN_SEQUENCE_FONT_SIZE, display.fontSize - 1) })
          }
        >
          A−
        </Button>
        <Button
          variant="outline-secondary"
          onClick={() =>
            onDisplayChange({ fontSize: Math.min(MAX_SEQUENCE_FONT_SIZE, display.fontSize + 1) })
          }
        >
          A＋
        </Button>
      </ButtonGroup>

      <Dropdown align="end">
        <Dropdown.Toggle size="sm" variant="outline-secondary" id="ga-display-menu">
          {t('geneAnalysis.toolbar.display')}
        </Dropdown.Toggle>
        <Dropdown.Menu className="ga-display-menu">
          <Dropdown.Header>{t('geneAnalysis.display.theme')}</Dropdown.Header>
          <Dropdown.Item active={display.theme === 'light'} onClick={() => onDisplayChange({ theme: 'light' })}>
            {t('geneAnalysis.display.theme_light')}
          </Dropdown.Item>
          <Dropdown.Item active={display.theme === 'dark'} onClick={() => onDisplayChange({ theme: 'dark' })}>
            {t('geneAnalysis.display.theme_dark')}
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Header>{t('geneAnalysis.display.guanine')}</Dropdown.Header>
          <Dropdown.Item active={display.guanine === 'yellow'} onClick={() => onDisplayChange({ guanine: 'yellow' })}>
            {t('geneAnalysis.display.guanine_yellow')}
          </Dropdown.Item>
          <Dropdown.Item active={display.guanine === 'black'} onClick={() => onDisplayChange({ guanine: 'black' })}>
            {t('geneAnalysis.display.guanine_black')}
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Header>{t('geneAnalysis.display.contrast')}</Dropdown.Header>
          <Dropdown.Item active={display.contrast === 'spec'} onClick={() => onDisplayChange({ contrast: 'spec' })}>
            {t('geneAnalysis.display.contrast_spec')}
          </Dropdown.Item>
          <Dropdown.Item active={display.contrast === 'aa'} onClick={() => onDisplayChange({ contrast: 'aa' })}>
            {t('geneAnalysis.display.contrast_aa')}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
